/**
 * Client Portal API
 * Client Portal OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {ApprovalRule} from '../models/ApprovalRule';
import type {ApprovalRuleUpdateRequest} from '../models/ApprovalRuleUpdateRequest';
import type {CreateRuleRequest} from '../models/CreateRuleRequest';
import type {Error} from '../models/Error';
import type {VendorApprovalRule} from '../models/VendorApprovalRule';

export interface CreateApprovalRule$Request {
    createRuleRequest: CreateRuleRequest;
}

export interface CreateApprovalRule$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const createApprovalRuleRequest = createAction<PayloadData<void, CreateApprovalRule$Request>>('APPROVAL_RULE__CREATE_APPROVAL_RULE__REQUEST');
export const createApprovalRuleSuccess = createAction<PayloadData<ApprovalRule, CreateApprovalRule$Request>>('APPROVAL_RULE__CREATE_APPROVAL_RULE__SUCCESS');
export const createApprovalRuleFailure = createAction<PayloadData<Error, CreateApprovalRule$Request, true>>('APPROVAL_RULE__CREATE_APPROVAL_RULE__FAILURE');

export const createApprovalRuleActions = [
    createApprovalRuleRequest,
    createApprovalRuleSuccess,
    createApprovalRuleFailure
] as const;

/**
 * Create a new approval rule.
 * @param {CreateRuleRequest} meta.createRuleRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const createApprovalRule = (meta: CreateApprovalRule$Request, config?: CreateApprovalRule$Config) => {
    return {
        CALL_API: {
            types: createApprovalRuleActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/rules`,
            TRANSPORT_REQUEST_BODY: meta.createRuleRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface DeleteApprovalRule$Request {
    ruleId: string;
}

export interface DeleteApprovalRule$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const deleteApprovalRuleRequest = createAction<PayloadData<void, DeleteApprovalRule$Request>>('APPROVAL_RULE__DELETE_APPROVAL_RULE__REQUEST');
export const deleteApprovalRuleSuccess = createAction<PayloadData<void, DeleteApprovalRule$Request>>('APPROVAL_RULE__DELETE_APPROVAL_RULE__SUCCESS');
export const deleteApprovalRuleFailure = createAction<PayloadData<Error, DeleteApprovalRule$Request, true>>('APPROVAL_RULE__DELETE_APPROVAL_RULE__FAILURE');

export const deleteApprovalRuleActions = [
    deleteApprovalRuleRequest,
    deleteApprovalRuleSuccess,
    deleteApprovalRuleFailure
] as const;

/**
 * delete an existing approval rule.
 * @param {string} meta.ruleId The ID of the approval rule to delete
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const deleteApprovalRule = (meta: DeleteApprovalRule$Request, config?: DeleteApprovalRule$Config) => {
    return {
        CALL_API: {
            types: deleteApprovalRuleActions,
            method: ApiMethods.DELETE,
            endpoint: `/web-api/rules/${meta.ruleId}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetApprovalRuleById$Request {
    ruleId: string;
}

export interface GetApprovalRuleById$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getApprovalRuleByIdRequest = createAction<PayloadData<void, GetApprovalRuleById$Request>>('APPROVAL_RULE__GET_APPROVAL_RULE_BY_ID__REQUEST');
export const getApprovalRuleByIdSuccess = createAction<PayloadData<ApprovalRule, GetApprovalRuleById$Request>>('APPROVAL_RULE__GET_APPROVAL_RULE_BY_ID__SUCCESS');
export const getApprovalRuleByIdFailure = createAction<PayloadData<Error, GetApprovalRuleById$Request, true>>('APPROVAL_RULE__GET_APPROVAL_RULE_BY_ID__FAILURE');

export const getApprovalRuleByIdActions = [
    getApprovalRuleByIdRequest,
    getApprovalRuleByIdSuccess,
    getApprovalRuleByIdFailure
] as const;

/**
 * get an existing approval rule by ID.
 * @param {string} meta.ruleId The ID of the approval rule to retrieve
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getApprovalRuleById = (meta: GetApprovalRuleById$Request, config?: GetApprovalRuleById$Config) => {
    return {
        CALL_API: {
            types: getApprovalRuleByIdActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/rules/${meta.ruleId}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetRulesByVendorId$Request {
    vendorId: string;
}

export interface GetRulesByVendorId$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getRulesByVendorIdRequest = createAction<PayloadData<void, GetRulesByVendorId$Request>>('APPROVAL_RULE__GET_RULES_BY_VENDOR_ID__REQUEST');
export const getRulesByVendorIdSuccess = createAction<PayloadData<Array<VendorApprovalRule>, GetRulesByVendorId$Request>>('APPROVAL_RULE__GET_RULES_BY_VENDOR_ID__SUCCESS');
export const getRulesByVendorIdFailure = createAction<PayloadData<Error, GetRulesByVendorId$Request, true>>('APPROVAL_RULE__GET_RULES_BY_VENDOR_ID__FAILURE');

export const getRulesByVendorIdActions = [
    getRulesByVendorIdRequest,
    getRulesByVendorIdSuccess,
    getRulesByVendorIdFailure
] as const;

/**
 * Get approval rules by vendor ID.
 * @param {string} meta.vendorId The ID of the approval rule to delete
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getRulesByVendorId = (meta: GetRulesByVendorId$Request, config?: GetRulesByVendorId$Config) => {
    return {
        CALL_API: {
            types: getRulesByVendorIdActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/vendors/${meta.vendorId}/rules`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateApprovalRule$Request {
    ruleId: string;
    approvalRuleUpdateRequest: ApprovalRuleUpdateRequest;
}

export interface UpdateApprovalRule$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateApprovalRuleRequest = createAction<PayloadData<void, UpdateApprovalRule$Request>>('APPROVAL_RULE__UPDATE_APPROVAL_RULE__REQUEST');
export const updateApprovalRuleSuccess = createAction<PayloadData<ApprovalRule, UpdateApprovalRule$Request>>('APPROVAL_RULE__UPDATE_APPROVAL_RULE__SUCCESS');
export const updateApprovalRuleFailure = createAction<PayloadData<Error, UpdateApprovalRule$Request, true>>('APPROVAL_RULE__UPDATE_APPROVAL_RULE__FAILURE');

export const updateApprovalRuleActions = [
    updateApprovalRuleRequest,
    updateApprovalRuleSuccess,
    updateApprovalRuleFailure
] as const;

/**
 * update an existing approval rule.
 * @param {string} meta.ruleId The ID of the approval rule to update
 * @param {ApprovalRuleUpdateRequest} meta.approvalRuleUpdateRequest Approval rule object that needs to be updated
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateApprovalRule = (meta: UpdateApprovalRule$Request, config?: UpdateApprovalRule$Config) => {
    return {
        CALL_API: {
            types: updateApprovalRuleActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/rules/${meta.ruleId}`,
            TRANSPORT_REQUEST_BODY: meta.approvalRuleUpdateRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

